import { getAuthHeader } from "./authHeader";
import axios from "axios";
import { API_BASE_URL } from "../apiConstants";
import { requestErrorMessage } from "../utils/loadError";

// Helper function to handle errors consistently
const handleApiError = (error, context = "operation") => {
  // Validation text is kept; a 404 page, stack trace or axios status line is not.
  const wrapped = new Error(requestErrorMessage(error, `Agent ${context} failed.`));
  wrapped.status = error.response?.status;
  throw wrapped;
};

const agentApi = {
  /**
   * Fetches a list of agents (org-scoped via admin/users endpoint).
   * @param {object} params - Query parameters (e.g., limit, page, role)
   * @returns {Promise<object>} - Promise resolving to { data: [...] }
   */
  getAll: async (params = {}) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        params,
        ...authConfig,
      });
      return response.data || { data: [] };
    } catch (error) {
      handleApiError(error, "fetch all");
    }
  },

  /**
   * Fetches a single agent by ID.
   * @param {string} id - The ID of the agent.
   * @returns {Promise<object>} - Promise resolving to the agent object.
   */
  getById: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(
        `${API_BASE_URL}/users/${id}`,
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, `fetch (ID: ${id})`);
    }
  },

  /**
   * Updates an agent's details via admin endpoint.
   * @param {string} id - The ID of the agent to update.
   * @param {object} agentData - The updated data.
   * @returns {Promise<object>} - Promise resolving to the updated agent object.
   */
  update: async (id, agentData) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.put(
        `${API_BASE_URL}/admin/users/${id}`,
        agentData,
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, `update (ID: ${id})`);
    }
  },

  /**
   * Reads the authenticated agent's persisted availability.
   * `availability_status` is the word (available, busy, away, offline), not the account lifecycle enum.
   * @returns {Promise<object>}
   */
  getMyStatus: async () => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(
        `${API_BASE_URL}/agents/status`,
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "fetch my status");
    }
  },

  /**
   * Updates the currently authenticated agent's status.
   * @param {string} status - The new status (e.g., 'available', 'busy', 'offline').
   * @returns {Promise<object>} - Promise resolving to { id, status }.
   */
  updateMyStatus: async (status) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.put(
        `${API_BASE_URL}/agents/status`,
        { status },
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "update my status");
    }
  },
};

export default agentApi;
